import { Memorial, MemorialInfoUpdate, MemorialOverview } from "@models/Memorial";
import { TimelineEntry, TimelineUpdate } from "@models/Timeline";
import { GallerySection, GalleryUpdate } from "@models/Gallery";
import createFormData from "@util/create-form.data";
import { convertIdInArray, convertIdInObject } from "@util/convert-id-field";
import { transformGuestBook, transformMemorial, transformMemorialOverview, transformTributes } from "@util/body-parse";
import { Tribute } from "@models/Tribute";
import { GuestBookEntry } from "@models/GuestBook";
import { AxiosProgressEvent } from "axios";
import apiClient from "./apiClient";

interface MemorialOverviewWithId extends Omit<MemorialOverview, "id"> {
    _id: string;
}

interface MemorialWithId extends Omit<Memorial, "id"> {
    _id: string;
}

interface TimelineEntryWithId extends Omit<TimelineEntry, "id"> {
    _id: string;
}

interface GallerySectionWithId extends Omit<GallerySection, "id"> {
    _id: string;
}

export const fetchMemorials = async (): Promise<MemorialOverview[]> => {
    try {
        const response = await apiClient.get<MemorialOverviewWithId[]>("/memorial");
        const memorialsWithIds = convertIdInArray(response.data);
        // Transform each memorial to parse date fields correctly
        return memorialsWithIds.map(transformMemorialOverview);
    } catch (error) {
        console.error("Failed to fetch memorials", error);
        throw error;
    }
};

export const fetchMemorial = async (id: string): Promise<Memorial> => {
    try {
        const response = await apiClient.get<MemorialWithId>(`/memorial/${id}`);
        const memorialWithId = convertIdInObject(response.data);

        // Transform the memorial to parse date fields correctly
        return transformMemorial(memorialWithId);
    } catch (error) {
        console.error("Failed to fetch memorial", error);
        throw error;
    }
};

// Returns created memorial id
export const createMemorialFromService = async (serviceId: string): Promise<string> => {
    try {
        const response = await apiClient.post<string>("memorial/create-from-service", { serviceId });
        return response.data;
    } catch (error) {
        console.error("Failed to post to create memorial", error);
        throw error;
    }
};

export const createMemorial = async (
    memorialInfo: MemorialInfoUpdate,
    progressCallback?: (progressEvent: AxiosProgressEvent) => void,
): Promise<string> => {
    try {
        const formData = createFormData(memorialInfo);

        const response = await apiClient.post("/memorial", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
            onUploadProgress: progressCallback,
        });

        return response.data._id;
    } catch (error) {
        console.error("Failed to create memorial", error);
        throw error;
    }
};

export const deleteMemorial = async (id: string): Promise<void> => {
    try {
        console.log(`/memorial/${id}/delete`)
        await apiClient.patch(`/memorial/${id}/delete`);
    } catch (error) {
        console.error("Failed to delete memorial", error);
        throw error;
    }
};

export const updateMemorialInfo = async (
    id: string,
    updates: MemorialInfoUpdate,
    progressCallback?: (progressEvent: AxiosProgressEvent) => void,
): Promise<Memorial> => {
    try {
        const formData = createFormData(updates);

        const response = await apiClient.patch<MemorialWithId>(`/memorial/${id}/info`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
            onUploadProgress: progressCallback,
        });

        const memorialWithId = convertIdInObject(response.data);
        return transformMemorial(memorialWithId);
    } catch (error) {
        console.error("Failed to update memorial info", error);
        throw error;
    }
};

export const updateMemorialTimeline = async (
    id: string,
    updates: TimelineUpdate,
    progressCallback?: (progressEvent: AxiosProgressEvent) => void,
): Promise<TimelineEntry[]> => {
    try {
        const formData = createFormData(updates);

        const response = await apiClient.patch<TimelineEntryWithId[]>(`/memorial/${id}/timeline`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
            onUploadProgress: progressCallback,
        });
        return convertIdInArray(response.data);
    } catch (error) {
        console.error("Failed to update memorial timeline", error);
        throw error;
    }
};

export const updateMemorialGallery = async (
    id: string,
    updates: GalleryUpdate,
    progressCallback?: (progressEvent: AxiosProgressEvent) => void,
): Promise<GallerySection[]> => {
    try {
        const formData = createFormData(updates);

        const response = await apiClient.patch<GallerySectionWithId[]>(`/memorial/${id}/gallery`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
            onUploadProgress: progressCallback,
        });
        return convertIdInArray(response.data);
    } catch (error) {
        console.error("Failed to update memorial timeline", error);
        throw error;
    }
};

export const deleteTribute = async (memorialId: string, tributeId: string): Promise<Tribute[]> => {
    try {
        const response = await apiClient.delete(`/memorial/${memorialId}/tribute`, {
            params: { tributeId },
        });
        return transformTributes(response.data);
    } catch (error) {
        console.error("Failed to delete tribute", error);
        throw error;
    }
};

export const deleteGuestBookEntry = async (memorialId: string, entryId: string)
: Promise<GuestBookEntry[]> => {
    try {
        const response = await apiClient.delete(`/memorial/${memorialId}/guest-book`, {
            params: { entryId },
        });
        return transformGuestBook(response.data);
    } catch (error) {
        console.error("Failed to delete guest book entry", error);
        throw error;
    }
};

export const setMemorialAdmin = async (memorialId: string, adminEmail: string)
: Promise<void> => {
    try {
        await apiClient.patch(`/memorial/${memorialId}/set-admin`, { adminEmail });
    } catch (error) {
        console.error("Failed to set memorial admin", error);
        throw error;
    }
};

// Root admin only: upgrades a free memorial without payment
export const upgradeMemorial = async (memorialId: string): Promise<void> => {
    try {
        await apiClient.patch(`/memorial/${memorialId}/upgrade`);
    } catch (error) {
        console.error("Failed to upgrade memorial", error);
        throw error;
    }
};

// Optional answer from the purchase success page after a standard user upgrades a memorial
export const submitFuneralDirector = async (memorialId: string, name: string): Promise<void> => {
    try {
        await apiClient.patch(`/memorial/${memorialId}/funeral-director`, { name });
    } catch (error) {
        console.error("Failed to submit funeral director", error);
        throw error;
    }
};

export interface MemorialContact {
    name: string;
    email: string;
}

export const fetchMemorialContactsExport = async (): Promise<MemorialContact[]> => {
    try {
        const response = await apiClient.get<MemorialContact[]>("/memorial/export-contacts");
        return response.data;
    } catch (error) {
        console.error("Failed to fetch memorial contacts export", error);
        throw error;
    }
};
